/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import template from './site-group-analysis-page.component.html';
import {SiteApiService} from '../site/site-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {visiblePageNumbers} from '../common/directives/pagination/visible-page-numbers';
import {UIRouterModule} from '@uirouter/angular';
import {AppSharedModule} from '../common/app-shared.module';
import {AdminListModule} from '../common/directives/admin-list.module';
import {ReportWidgetsModule} from '../report/report-widgets.module';

interface AnalysisListColumn {
  param: string;
  label?: string;
  labelInstant?: string;
  numeric?: boolean;
  sortable?: boolean;
}

@Component({
  standalone: true,
  imports: [AppSharedModule, UIRouterModule, AdminListModule, ReportWidgetsModule],
  selector: 'adn-site-group-analysis-page',
  template
})
export class SiteGroupAnalysisPageComponent implements OnChanges, OnDestroy {
  private readonly listLifetime = inject(DestroyRef);
  @Input() model: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  activeTab: 'sites' | 'reports' = 'sites';
  visibleReports = false;
  canSeeReports = false;

  sites: any = {};
  get displayItems(): any[] { return this.sites?.listState?.items() || []; }
  get hasItems(): boolean { return this.displayItems.length > 0; }
  get listPageSize(): number { return this.sites?.listState?.pageSize || 25; }
  get listTotalCount(): number { return this.sites?.listState?.totalCount() || 0; }
  get listCurrentPage(): number { return this.sites?.listState?.currentPage() || 1; }
  readonly siteItemStateName = 'app.inv.sites.site';

  readonly siteColumns: AnalysisListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'siteUrl', labelInstant: 'Site URL'}
  ];

  private lastLoadedModelId: string | undefined;
  private destroyed = false;

  constructor(
    private siteApi: SiteApiService,
    private listBinding: SignalResourceListBindingService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any
  ) {
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model || changes.isNew) {
      this.refreshSitesIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  selectTab(tab: 'sites' | 'reports'): void {
    this.activeTab = tab;
    if (tab === 'reports') {
      this.visibleReports = true;
    }
  }

  isColumnSortable(column: AnalysisListColumn): boolean {
    return column.sortable !== false && !!column.param;
  }

  columnLabel(column: AnalysisListColumn): string {
    if (column.labelInstant) {
      return column.labelInstant;
    }
    const key = column.label || column.param;
    return key ? 'label.' + key : '';
  }

  sortBy(column: AnalysisListColumn): void {
    if (!this.isColumnSortable(column) || !this.sites?.makeSortQuery) {
      return;
    }
    if (column.numeric) {
      this.sites.makeSortQuery(column.param, column.numeric);
    } else {
      this.sites.makeSortQuery(column.param);
    }
  }

  changePage(page: number): void {
    if (!this.sites || page < 1 || page > this.totalPages() || page === this.listCurrentPage) {
      return;
    }
    this.sites.currentPage = page;
    this.sites.changePage();
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.listTotalCount / (this.listPageSize || 1)));
  }

  pageNumbers(): number[] {
    return visiblePageNumbers(this.listCurrentPage, this.totalPages());
  }

  pageRangeStart(): number {
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  pageRangeEnd(): number {
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }

  private refreshSitesIfNeeded(): void {
    const modelId = this.effectiveIsNew ? undefined : this.model?.id;
    if (modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    this.initSitesList();
  }

  private initSitesList(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.sites?.disposeList?.();
      this.sites = {};

      return;
    }

    this.sites?.disposeList?.();
    this.sites = {};

    this.sites = this.listBinding.create(this.siteApi, 'fullSitesForSiteGroup', this.listLifetime, {
      baseTable: 'site',
      moreParams: {siteGroup: this.model.id},

    });
  }
}
